import { Button, ButtonContainer, Heading, Layout, TileSelect, TileSelectGroup } from '@vwfs-bronson/bronson-react';
import React, { useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useHistory } from 'react-router-dom';
import { dashboardPagePath } from '../../../navigation/paths';
import { ContractEndOptionsView } from '../enums';
import { useAnalyticsActionTracker } from '@cp-shared-15/frontend-ui';

export enum RenewalOption {
    FINANCE_ONLINE = 'FINANCE_ONLINE',
    CONTACT_DEALER = 'CONTACT_DEALER',
}

type Tile = {
    value: RenewalOption;
    icon: string;
    title: string;
    description: string;
};

export const RenewalOptions: React.FC<{
    handleSubmit: (contractEndOptionsView: ContractEndOptionsView) => void;
    handleBackClick: (contractEndOptionsView: ContractEndOptionsView) => void;
    header: React.ReactNode;
}> = ({ handleSubmit, handleBackClick, header }) => {
    const { t } = useTranslation('contract-end-options');
    const history = useHistory();

    const [selectedOption, setSelectedOption] = useState<RenewalOption | undefined>();

    const { onAction: onFinanceOnlineSelected } = useAnalyticsActionTracker('onEndOfContractPcpRenewalSelected');
    const { onAction: onContactDealerSelected } = useAnalyticsActionTracker('onEndOfContractPcpDealerData');

    const tiles: Tile[] = useMemo(
        () => [
            {
                value: RenewalOption.FINANCE_ONLINE,
                icon: 'semantic-car',
                title: t(`renewal-options.options.finance-online.title`),
                description: t(`renewal-options.options.finance-online.description`),
            },
            {
                value: RenewalOption.CONTACT_DEALER,
                icon: 'semantic-location',
                title: t(`renewal-options.options.contact-dealer.title`),
                description: t(`renewal-options.options.contact-dealer.description`),
            },
        ],
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [t],
    );

    const getFinanceOnlineExternalLink = useCallback((): string => {
        return t(`renewal-options.options.finance-online.link`);
    }, [t]);

    const handleSubmitClick = useCallback((): void => {
        if (!selectedOption) {
            return;
        }

        switch (selectedOption) {
            case RenewalOption.FINANCE_ONLINE:
                onFinanceOnlineSelected();
                window.open(getFinanceOnlineExternalLink(), '_blank');
                history.push(dashboardPagePath());
                return;
            case RenewalOption.CONTACT_DEALER:
                onContactDealerSelected();
                handleSubmit(ContractEndOptionsView.DEALER_INFORMATION);
                return;
        }
    }, [
        selectedOption,
        onFinanceOnlineSelected,
        getFinanceOnlineExternalLink,
        history,
        onContactDealerSelected,
        handleSubmit,
    ]);

    const handleTileClick = useCallback((value: RenewalOption): void => {
        setSelectedOption(value);
    }, []);

    const handleBackButtonClick = useCallback((): void => {
        handleBackClick(ContractEndOptionsView.OPTIONS_OVERVIEW);
    }, [handleBackClick]);

    return (
        <Layout>
            <Layout.Item>
                <Heading className="u-mt" level={1}>
                    {t('renewal-options.headline')}
                </Heading>
            </Layout.Item>
            <Layout.Item>{header}</Layout.Item>
            <Layout.Item>
                <Heading className="u-mt" level={3} id="renewal-options-heading">
                    {t('renewal-options.sub-headline')}
                </Heading>
                <p>{t('renewal-options.description')}</p>
            </Layout.Item>
            <Layout.Item>
                <TileSelectGroup
                    title={{ referenceId: 'renewal-options-heading' }}
                    key="RENEWAL"
                    layoutItemClassName="u-1/2 u-1/1@s"
                    className={'u-text-center'}
                    testId={'tile-select-group-renewal'}
                >
                    {tiles.map(({ value, title, description, icon }) => (
                        <TileSelect
                            key={value}
                            inputType="radio"
                            radioGroupName="RENEWAL"
                            onClick={(): void => handleTileClick(value)}
                            value={value}
                            title={title}
                            icon={icon}
                            testId={value}
                        >
                            {description}
                        </TileSelect>
                    ))}
                </TileSelectGroup>
            </Layout.Item>
            <Layout.Item>
                <ButtonContainer center className={'u-mt'}>
                    <Button secondary testId="backButton" onClick={handleBackButtonClick}>
                        {t('translation:editable-section-nav.back')}
                    </Button>
                    <Button testId="submitButton" disabled={!selectedOption} onClick={handleSubmitClick}>
                        {t('translation:editable-section-nav.submit')}
                    </Button>
                </ButtonContainer>
            </Layout.Item>
        </Layout>
    );
};
